import React, { useState, useEffect } from "react"
import { Modal, Form } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlay } from "@fortawesome/free-solid-svg-icons"
import {
  FormParameterValues,
  ScriptExecutionParameters,
  OutputProject,
} from "types/automation.types"
import { useScriptMetadataQuery } from "features/Automation/api/useAutomationQuery"
import { useProjectsQuery } from "features/Projects/api/useProjectsQuery"
import { PROJECT_FILTER } from "types/project.types"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import DynamicParameterForm from "./DynamicParameterForm.component"

interface RunAutomationModalProps {
  scriptName: string
  isVisible: boolean
  cancel: () => void
  onExecute: (
    scriptName: string,
    parameters?: ScriptExecutionParameters,
    outputProject?: OutputProject
  ) => void
}

const RunAutomationModal: React.FC<RunAutomationModalProps> = (props) => {
  const { scriptName, isVisible, cancel, onExecute } = props

  const [form] = Form.useForm<FormParameterValues>()
  const [submitting, setSubmitting] = useState(false)

  // Projects for the output-project selector; fetched while the modal is open.
  const projectsQuery = useProjectsQuery(
    PROJECT_FILTER.ALL,
    isVisible && Boolean(scriptName)
  )
  const projects = projectsQuery.data ?? []

  // Script metadata (parameter definitions); cached across modal reopens.
  const metadataQuery = useScriptMetadataQuery(scriptName, isVisible)
  const scriptMetadata = metadataQuery.data ?? null
  const loading = metadataQuery.isLoading

  // Reset form when modal closes
  useEffect(() => {
    if (!isVisible) {
      form.resetFields()
    }
  }, [isVisible, form])

  const handleRunScript = async () => {
    if (!scriptName) return

    try {
      setSubmitting(true)

      // Always validate form since output project is required
      const values = await form.validateFields()

      // Extract output project information
      const outputProjectId = values.output_project
      if (!outputProjectId) {
        notify.toast.error("Output project is required")
        return
      }

      const selectedProject = projects.find((p) => p.id === outputProjectId)
      if (!selectedProject) {
        notify.toast.error("Selected output project not found")
        return
      }

      const outputProject: OutputProject = {
        project_id: selectedProject.id,
        owner: selectedProject.owner,
      }

      // Transform form values to execution parameters (excluding output_project)
      const executionParameters: ScriptExecutionParameters = {}
      Object.entries(values).forEach(([key, value]) => {
        // Skip output_project as it's handled separately
        if (key === "output_project") return

        if (value instanceof Date) {
          executionParameters[key] = value.toISOString()
        } else if (Array.isArray(value)) {
          // Handle multiple project selections - convert array to comma-separated string
          executionParameters[key] = value.join(",")
        } else if (value !== undefined && value !== null) {
          executionParameters[key] = value
        }
      })

      // Execute script with parameters and output project
      onExecute(scriptName, executionParameters, outputProject)
    } catch (error) {
      if (error && typeof error === "object" && "errorFields" in error) {
        notify.toast.error("Please fill in all required fields")
      } else {
        console.error("Error running script:", error)
        notify.toast.error("Failed to run script")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    cancel()
  }

  return (
    <Modal
      centered
      title={
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faPlay} style={{ color: "#36c3ed" }} />
          <span>Run Script: {scriptName}</span>
        </div>
      }
      open={isVisible}
      closable={false}
      footer={null}
      width={600}
    >
      <div className="flex flex-col mb-4">
        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            Configure settings for <strong>{scriptName}</strong>:
          </p>
          <div className="bg-gray-50 p-3 rounded border mb-4">
            <p className="text-sm text-gray-600 mb-1">
              <strong>Script:</strong> {scriptName}
            </p>
            <p className="text-sm text-gray-600 mb-1">
              <strong>Type:</strong> {scriptMetadata?.script_type || "Unknown"}
            </p>
            {scriptMetadata?.description && (
              <p className="text-sm text-gray-600 mb-1">
                <strong>Description:</strong> {scriptMetadata.description}
              </p>
            )}
            {scriptMetadata?.timeout_minutes && (
              <p className="text-sm text-gray-600">
                <strong>Timeout:</strong> {scriptMetadata.timeout_minutes}{" "}
                minutes
              </p>
            )}
          </div>
        </div>

        {/* Dynamic Parameter Form (now always includes output project) */}
        {loading ? (
          <div className="text-center py-4 text-gray-500">
            Loading script parameters...
          </div>
        ) : (
          <DynamicParameterForm
            parameters={scriptMetadata?.parameters || []}
            form={form}
          />
        )}
      </div>

      <div className="flex flex-row space-x-2">
        <DataQButton
          className="ml-auto"
          type="secondary"
          onClick={handleCancel}
          isDisabled={submitting}
        >
          Cancel
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={handleRunScript}
          isDisabled={!scriptName || loading}
          isLoading={submitting}
        >
          {submitting ? "Running..." : "Run Script"}
        </DataQButton>
      </div>
    </Modal>
  )
}

export default RunAutomationModal
